/**
 * Krono Product — client-side state machine for the guided purchase flow.
 *
 * Responsibilities:
 * - Initialize Swiper instances (up to 4 on desktop, 2 on mobile)
 * - Variant selection state management
 * - IntersectionObserver scroll spy for left panel switching
 * - GSAP scrollTo for Back/Continue navigation
 * - Add to Cart / Direct Checkout via AJAX /cart/add.js
 * - Summary price updates
 * - Ship-to country detection
 * - Zoom modal integration
 * - Mobile detection and responsive behavior
 *
 * Dependencies: GSAP (gsap.min.js, ScrollToPlugin), Swiper (swiper.min.js)
 */

(function () {
	"use strict";

	// =========================================================================
	// Guard: require GSAP, Swiper, and global data
	// =========================================================================
	if (typeof Swiper === "undefined") {
		console.warn("[Krono] Swiper is not loaded. Aborting initialization.");
		return;
	}
	if (typeof gsap === "undefined") {
		console.warn("[Krono] GSAP is not loaded. Aborting initialization.");
		return;
	}
	if (
		typeof window.__KRONO_MAIN_PRODUCT__ === "undefined" ||
		typeof window.__KRONO_GIFT_PRODUCT__ === "undefined"
	) {
		console.warn(
			"[Krono] Global product data not found. Aborting initialization.",
		);
		return;
	}

	// =========================================================================
	// State
	// =========================================================================
	var state = {
		bundleId: null,
		mainVariantId: null,
		mainPriceCents: 0,
		mainCompareAtCents: 0,
		giftVariantId: null,
		giftPriceCents: 0,
		giftCompareAtCents: 0,
		currentStep: "main",
		isLoading: false,
		isMobile: window.innerWidth < 751,
	};

	// =========================================================================
	// Swiper instances references
	// =========================================================================
	var swipers = {
		mainLeftBlack: null,
		mainLeftWhite: null,
		giftLeft: null,
		giftLeftThumbs: null,
		mainRightBlack: null,
		mainRightWhite: null,
		giftRight: null,
		giftRightThumbs: null,
	};

	// =========================================================================
	// AbortController for cleanup
	// =========================================================================
	var abortController = new AbortController();
	var signal = abortController.signal;

	// =========================================================================
	// Utility: Format cents to money string (simple fallback)
	// =========================================================================
	function formatMoney(cents) {
		// Use the first variant's price format as template if available
		var prices = window.__KRONO_MAIN_PRICES__;
		var templatePrice = null;
		for (var key in prices) {
			if (prices.hasOwnProperty(key)) {
				templatePrice = prices[key].price;
				break;
			}
		}
		if (!templatePrice) {
			return "$" + (cents / 100).toFixed(2);
		}
		// Format using the Shopify money format pattern
		var amount = (cents / 100).toFixed(2);
		// Try to match the template currency symbol and format
		var match = templatePrice.match(/^([^\d]*)([\d,]+\.?\d*)([^\d]*)$/);
		if (match) {
			return match[1] + amount + match[3];
		}
		return "$" + amount;
	}

	// =========================================================================
	// Utility: Find variant by selected options
	// =========================================================================
	function findVariantByOptions(variants, selectedOptions) {
		return (
			variants.find(function (variant) {
				return variant.options.every(function (opt, idx) {
					return opt === selectedOptions[idx];
				});
			}) || null
		);
	}

	// =========================================================================
	// Utility: Get selected option values from a picker
	// =========================================================================
	function getSelectedOptions(pickerEl) {
		var optionGroups = pickerEl.querySelectorAll(".option-group");
		var selected = [];
		optionGroups.forEach(function (group, index) {
			// Check for active state across all button types

			var activeBtn =
				group.querySelector(".krono-option-btn.bx-border-theme-pink") ||
				group.querySelector(".krono-color-swatch--active") ||
				group.querySelector(".krono-ship-btn--active");
			if (activeBtn) {
				selected[index] = activeBtn.getAttribute("data-option-value");
			} else {
				// Fall back to first available button (any type)
				var firstBtn = group.querySelector(
					".krono-variant-btn:not([disabled])",
				);
				if (firstBtn) {
					selected[index] =
						firstBtn.getAttribute("data-option-value");
				}
			}
		});

		return selected;
	}

	// =========================================================================
	// Color Swiper Toggle — fade between black- and white-image swiper sets
	// =========================================================================
	function toggleColorSwipers(color) {
		var blackEls = document.querySelectorAll('[data-color-swiper="black"]');
		var whiteEls = document.querySelectorAll('[data-color-swiper="white"]');
		var isBlack = color === "black";

		blackEls.forEach(function (el) {
			el.style.opacity = isBlack ? "1" : "0";
			el.style.pointerEvents = isBlack ? "auto" : "none";
			el.style.zIndex = isBlack ? "1" : "0";
		});
		whiteEls.forEach(function (el) {
			el.style.opacity = isBlack ? "0" : "1";
			el.style.pointerEvents = isBlack ? "none" : "auto";
			el.style.zIndex = isBlack ? "0" : "1";
		});
	}

	// =========================================================================
	// Swiper Initialization
	// =========================================================================
	function initSwiper(containerId, thumbsContainerSelector) {
		var container = document.getElementById(containerId);
		if (!container) return null;

		var mainEl = container.querySelector(".krono-main-swiper");
		var thumbsEl = container.querySelector(".krono-thumbs-swiper");
		if (!mainEl) return null;

		// Dots mode (left panel) — skip thumbs swiper, wire dot clicks instead
		var useDots = container.getAttribute("data-show-dots") === "true";

		var thumbsSwiper = null;
		if (thumbsEl && !useDots) {
			thumbsSwiper = new Swiper(thumbsEl, {
				spaceBetween: 8,
				slidesPerView: 4,
				freeMode: true,
				watchSlidesProgress: true,
				slideToClickedSlide: true,
				breakpoints: {
					751: {
						slidesPerView: 5,
						spaceBetween: 10,
					},
				},
			});
		}

		var swiperConfig = {
			slidesPerView: 1,
			spaceBetween: 12,
			navigation: {
				nextEl: container.querySelector(".swiper-button-next"),
				prevEl: container.querySelector(".swiper-button-prev"),
			},
			keyboard: { enabled: true },
			loop: false,
			touchReleaseOnEdges: true,
			on: {},
		};

		if (thumbsSwiper) {
			swiperConfig.thumbs = { swiper: thumbsSwiper };
		}

		if (useDots) {
			// Initialize active dot to match current slide, and keep it
			// in sync on every navigation (click, arrow, swipe, keyboard).
			swiperConfig.on.slideChange = function () {
				syncDotsActive(container, this.activeIndex);
			};
		}

		var mainSwiper = new Swiper(mainEl, swiperConfig);

		if (useDots) {
			wireDotClicks(container, mainSwiper);
			syncDotsActive(container, mainSwiper.activeIndex);
		}

		return { main: mainSwiper, thumbs: thumbsSwiper };
	}

	// -------------------------------------------------------------------------
	// Dots — click-to-jump + active-state sync
	// -------------------------------------------------------------------------
	function wireDotClicks(container, mainSwiper) {
		var dots = container.querySelectorAll("[data-krono-dot-index]");
		dots.forEach(function (dot) {
			dot.addEventListener(
				"click",
				function (e) {
					e.preventDefault();
					var idx = parseInt(
						dot.getAttribute("data-krono-dot-index"),
						10,
					);
					if (!isNaN(idx) && mainSwiper && !mainSwiper.destroyed) {
						mainSwiper.slideTo(idx);
					}
				},
				{ signal: signal },
			);
		});
	}

	function syncDotsActive(container, activeIndex) {
		var dots = container.querySelectorAll("[data-krono-dot-index]");
		dots.forEach(function (dot) {
			var idx = parseInt(dot.getAttribute("data-krono-dot-index"), 10);
			var isActive = idx === activeIndex;
			dot.classList.toggle("krono-dot--active", isActive);
			dot.setAttribute("aria-current", isActive ? "true" : "false");
		});
	}

	function initAllSwipers() {
		// Desktop left panel swipers
		if (!state.isMobile) {
			var mainLeftBlack = initSwiper("krono-main-left-black");
			if (mainLeftBlack) {
				swipers.mainLeftBlack = mainLeftBlack.main;
			}
			var mainLeftWhite = initSwiper("krono-main-left-white");
			if (mainLeftWhite) {
				swipers.mainLeftWhite = mainLeftWhite.main;
			}
			var giftLeft = initSwiper("krono-gift-left");
			if (giftLeft) {
				swipers.giftLeft = giftLeft.main;
				swipers.giftLeftThumbs = giftLeft.thumbs;
			}
		}

		// Right panel swipers (always initialized, used on both desktop and mobile)
		var mainRightBlack = initSwiper("krono-main-right-black");
		if (mainRightBlack) {
			swipers.mainRightBlack = mainRightBlack.main;
		}
		var mainRightWhite = initSwiper("krono-main-right-white");
		if (mainRightWhite) {
			swipers.mainRightWhite = mainRightWhite.main;
		}
		var giftRight = initSwiper("krono-gift-right");
		if (giftRight) {
			swipers.giftRight = giftRight.main;
			swipers.giftRightThumbs = giftRight.thumbs;
		}
	}

	// =========================================================================
	// Variant Selection
	// =========================================================================
	function initVariantPickers() {
		// Main picker
		var mainPicker = document.getElementById("krono-main");
		if (mainPicker) {
			mainPicker.addEventListener(
				"click",
				function (e) {
					var btn = e.target.closest(
						".krono-option-btn, .krono-color-swatch, .krono-ship-btn",
					);
					if (!btn || btn.disabled) return;

					handleVariantSelect(mainPicker, btn, "main");
				},
				{ signal: signal },
			);
		}

		// Gift picker
		var giftPicker = document.getElementById("krono-gift");
		if (giftPicker) {
			giftPicker.addEventListener(
				"click",
				function (e) {
					var btn = e.target.closest(
						".krono-option-btn, .krono-color-swatch, .krono-ship-btn",
					);
					if (!btn || btn.disabled) return;

					// Block if main not yet selected
					if (!state.mainVariantId) return;

					handleVariantSelect(giftPicker, btn, "gift");
				},
				{ signal: signal },
			);
		}
	}

	function handleVariantSelect(pickerEl, clickedBtn, role) {
		// Update button active states within the same option group
		var optionName = clickedBtn.getAttribute("data-option-name");
		var optionValue = clickedBtn.getAttribute("data-option-value");
		var parentGroup = clickedBtn.closest(".option-group");

		if (parentGroup) {
			// Remove active state from all sibling variant buttons (all types)
			parentGroup
				.querySelectorAll(".krono-variant-btn")
				.forEach(function (btn) {
					// Default pill button active classes
					btn.classList.remove(
						"bx-border-theme-pink",
						"bx-bg-pink-50",
						"bx-text-theme-pink",
					);
					// Color swatch active class
					btn.classList.remove("krono-color-swatch--active");
					// Ship-from button active class
					btn.classList.remove("krono-ship-btn--active");
				});
		}

		// Add active state based on button type
		if (clickedBtn.classList.contains("krono-color-swatch")) {
			clickedBtn.classList.add("krono-color-swatch--active");
		} else if (clickedBtn.classList.contains("krono-ship-btn")) {
			clickedBtn.classList.add("krono-ship-btn--active");
		} else {
			// Default pill button
			clickedBtn.classList.add(
				"bx-border-theme-pink",
				"bx-bg-pink-50",
				"bx-text-theme-pink",
			);
		}

		// Update the selected value label
		var label = parentGroup
			? parentGroup.querySelector(".option-selected-value")
			: null;
		if (label) {
			label.textContent =
				clickedBtn.classList.contains("krono-color-swatch") ||
				clickedBtn.classList.contains("krono-ship-btn")
					? optionValue.toUpperCase()
					: optionValue;
		}

		// GSAP micro-bounce feedback
		if (typeof gsap !== "undefined") {
			gsap.fromTo(
				clickedBtn,
				{ scale: 1 },
				{ scale: 1.05, duration: 0.1, yoyo: true, repeat: 1 },
			);
		}

		// Find matching variant
		var variants =
			role === "main"
				? window.__KRONO_MAIN_VARIANTS__
				: window.__KRONO_GIFT_VARIANTS__;
		var prices =
			role === "main"
				? window.__KRONO_MAIN_PRICES__
				: window.__KRONO_GIFT_PRICES__;

		var selectedOptions = getSelectedOptions(pickerEl);
		var matchedVariant = findVariantByOptions(variants, selectedOptions);

		if (matchedVariant) {
			// Update state
			if (role === "main") {
				state.mainVariantId = matchedVariant.id;
				if (prices[matchedVariant.id]) {
					state.mainPriceCents =
						prices[matchedVariant.id].price_cents;
					state.mainCompareAtCents =
						prices[matchedVariant.id].compare_at_cents;
				}
				// Enable gift picker
				enableGiftPicker();
				// Update main price display
				updateMainPriceDisplay(prices[matchedVariant.id]);
			} else {
				state.giftVariantId = matchedVariant.id;
				if (prices[matchedVariant.id]) {
					state.giftPriceCents =
						prices[matchedVariant.id].price_cents;
					state.giftCompareAtCents =
						prices[matchedVariant.id].compare_at_cents;
				}
				// Enable gift continue button
				var giftContinue = document.getElementById(
					"krono-gift-continue",
				);
				if (giftContinue) giftContinue.disabled = false;
			}

			// Update hidden input
			var hiddenInput = pickerEl.querySelector('input[type="hidden"]');
			if (hiddenInput) hiddenInput.value = matchedVariant.id;

			// Update summary if applicable
			updateSummary();

			// Slide swipers to variant's corresponding image
			// Main product: toggle color swiper set + slide within active color
			// Gift product: standard slide-to across all images
			if (
				matchedVariant.featured_media &&
				matchedVariant.featured_media.id
			) {
				var product =
					role === "main"
						? window.__KRONO_MAIN_PRODUCT__
						: window.__KRONO_GIFT_PRODUCT__;
				var media = product && product.media;
				if (media && media.length) {
					var targetId = matchedVariant.featured_media.id;
					var imageIndex = -1;
					for (var i = 0; i < media.length; i++) {
						if (media[i].id === targetId) {
							imageIndex = i;
							break;
						}
					}
					if (imageIndex >= 0) {
						if (role === "main") {
							// Determine color from selected options
							var colorValue = selectedOptions[0];
							if (colorValue) {
								toggleColorSwipers(colorValue.toLowerCase());
								// Map global index (0-5) → within-color-swiper index (0-2)
								var cIdx =
									colorValue.toLowerCase() === "white"
										? imageIndex - 3
										: imageIndex;
								if (cIdx >= 0 && cIdx < 3) {
									var ls =
										colorValue.toLowerCase() === "white"
											? swipers.mainLeftWhite
											: swipers.mainLeftBlack;
									var rs =
										colorValue.toLowerCase() === "white"
											? swipers.mainRightWhite
											: swipers.mainRightBlack;
									if (ls && !ls.destroyed) ls.slideTo(cIdx);
									if (rs && !rs.destroyed) rs.slideTo(cIdx);
								}
							}
						} else {
							var leftSwiper = swipers.giftLeft;
							var rightSwiper = swipers.giftRight;
							if (leftSwiper && !leftSwiper.destroyed) {
								leftSwiper.slideTo(imageIndex);
							}
							if (rightSwiper && !rightSwiper.destroyed) {
								rightSwiper.slideTo(imageIndex);
							}
						}
					}
				}
			}
		}
	}

	function enableGiftPicker() {
		var wrapper = document.getElementById("krono-gift-picker-wrapper");
		var hint = document.getElementById("krono-gift-disabled-hint");
		if (wrapper) {
			wrapper.classList.remove("bx-opacity-50", "bx-pointer-events-none");
		}
		if (hint) {
			hint.style.display = "none";
		}
	}

	function updateMainPriceDisplay(priceData) {
		if (!priceData) return;
		var priceEl = document.querySelector("[data-krono-main-price]");
		if (priceEl) {
			priceEl.textContent = priceData.price;
		}
		var giftOrigPriceEl = document.querySelector(
			"[data-krono-gift-original-price]",
		);
		if (giftOrigPriceEl && priceData.compare_at_price) {
			giftOrigPriceEl.textContent = priceData.compare_at_price;
		}
	}

	// =========================================================================
	// Summary Updates
	// =========================================================================
	function updateSummary() {
		if (!state.mainVariantId || !state.giftVariantId) return;

		var savings = state.giftPriceCents;
		var finalPrice = state.mainPriceCents;

		// Right panel summary
		var savingsEl = document.querySelector("[data-summary-savings]");
		var finalPriceEl = document.querySelector("[data-summary-final-price]");

		if (savingsEl) savingsEl.textContent = "-" + formatMoney(savings);
		if (finalPriceEl) finalPriceEl.textContent = formatMoney(finalPrice);

		// Update summary images and variant names
		updateSummaryCards();

		// Enable action buttons
		var addCartBtn = document.getElementById("krono-add-cart");
		var checkoutBtn = document.getElementById("krono-direct-checkout");
		if (addCartBtn) addCartBtn.disabled = false;
		if (checkoutBtn) checkoutBtn.disabled = false;
	}

	function updateSummaryCards() {
		var mainProduct = window.__KRONO_MAIN_PRODUCT__;
		var giftProduct = window.__KRONO_GIFT_PRODUCT__;

		// Main product card
		var mainVariant = findVariantById(
			window.__KRONO_MAIN_VARIANTS__,
			state.mainVariantId,
		);
		var mainPrice = window.__KRONO_MAIN_PRICES__[state.mainVariantId];

		if (mainVariant) {
			var colorName = mainVariant.options
				? mainVariant.options[0]
				: mainVariant.title;
			var mainTitle = colorName + " Krono";
			setElText("[data-summary-name-main-left]", mainTitle);
			setElText("[data-summary-name-main-right]", mainTitle);
		}

		if (mainPrice) {
			setElText("[data-summary-price-main]", mainPrice.price);
			setElText("[data-summary-price-main-left]", mainPrice.price);
			setElText("[data-summary-price-main-right]", mainPrice.price);
		}

		// Main product image
		if (mainVariant && mainVariant.featured_image) {
			setElSrc(
				"[data-summary-image-main]",
				mainVariant.featured_image.src,
			);
			setElSrc(
				"[data-summary-image-main-left]",
				mainVariant.featured_image.src,
			);
			setElSrc(
				"[data-summary-image-main-right]",
				mainVariant.featured_image.src,
			);
		} else if (mainProduct.featured_image) {
			setElSrc("[data-summary-image-main]", mainProduct.featured_image);
			setElSrc(
				"[data-summary-image-main-left]",
				mainProduct.featured_image,
			);
			setElSrc(
				"[data-summary-image-main-right]",
				mainProduct.featured_image,
			);
		}

		// Gift product card
		var giftVariant = findVariantById(
			window.__KRONO_GIFT_VARIANTS__,
			state.giftVariantId,
		);
		var giftPrice = window.__KRONO_GIFT_PRICES__[state.giftVariantId];

		if (giftVariant) {
			var giftColorName = giftVariant.options
				? giftVariant.options[0]
				: giftVariant.title;
			var giftTitle = giftColorName + " Case";
			setElText("[data-summary-name-gift-left]", giftTitle);
			setElText("[data-summary-name-gift-right]", giftTitle);
		}
		if (giftPrice) {
			setElText("[data-summary-price-gift]", giftPrice.price);
			setElText("[data-summary-price-gift-left]", giftPrice.price);
			setElText("[data-summary-price-gift-right]", giftPrice.price);
		}

		// Gift product image
		if (giftVariant && giftVariant.featured_image) {
			setElSrc(
				"[data-summary-image-gift]",
				giftVariant.featured_image.src,
			);
			setElSrc(
				"[data-summary-image-gift-left]",
				giftVariant.featured_image.src,
			);
			setElSrc(
				"[data-summary-image-gift-right]",
				giftVariant.featured_image.src,
			);
		} else if (giftProduct.featured_image) {
			setElSrc("[data-summary-image-gift]", giftProduct.featured_image);
			setElSrc(
				"[data-summary-image-gift-left]",
				giftProduct.featured_image,
			);
			setElSrc(
				"[data-summary-image-gift-right]",
				giftProduct.featured_image,
			);
		}
	}

	function setElText(selector, text) {
		var el = document.querySelector(selector);
		if (el) el.textContent = text;
	}

	function setElSrc(selector, src) {
		var el = document.querySelector(selector);
		if (el && src) el.src = src;
	}

	function findVariantById(variants, id) {
		for (var i = 0; i < variants.length; i++) {
			if (variants[i].id === id) return variants[i];
		}
		return null;
	}

	// =========================================================================
	// Directional Reading-Zone Scroll Spy
	// =========================================================================
	// A scroll-driven (rAF-batched) step detector that picks the active
	// step using a *directional* reading zone, not a fixed threshold.
	// The intuition:
	//   • When the user scrolls DOWN, each new step enters the viewport
	//     from the bottom. The step is "active" while its title sits in
	//     the BOTTOM 1/3 of the viewport — the strip where a newly
	//     arrived step first becomes legible.
	//   • When the user scrolls UP, each previously-passed step re-enters
	//     the viewport from the top. The step is "active" while its
	//     title sits in the TOP 1/3 of the viewport — the strip where a
	//     re-emerging step first comes back into focus.
	// When multiple titles fall in the same zone, the most recently
	// entered one wins (largest top when scrolling down, smallest top
	// when scrolling up). When no title is in the zone, the current
	// active step is preserved (no flicker / no forced null).
	function initScrollSpy() {
		if (state.isMobile) return; // mobile: left panel hidden, no switching

		var stepEls = document.querySelectorAll(".krono-step");
		var stickyContainer = document.getElementById("krono-left-sticky");
		var stickyInner = document.getElementById("krono-sticky-inner");
		var panels = {
			main: document.querySelector('[data-panel="main"]'),
			gift: document.querySelector('[data-panel="gift"]'),
			summary: document.querySelector('[data-panel="summary"]'),
		};

		if (!stepEls.length || !stickyInner) return;

		// Pre-resolve the title element per step so the threshold read
		// is O(1) per frame, not a querySelector on each callback.
		var titles = Array.prototype.map.call(stepEls, function (step) {
			return step.querySelector("[data-step-title]");
		});

		// Init: main active
		var activeKey = "main";
		Object.keys(panels).forEach(function (key) {
			setPanelActive(panels[key], key === "main");
		});
		stepEls.forEach(function (step) {
			step.classList.toggle(
				"is-active",
				step.getAttribute("data-step") === "main",
			);
		});

		// Direction tracking. Seed with the current scroll position so
		// the first rAF tick reads a meaningful delta (not 0 → 0).
		var lastScrollY = window.scrollY || window.pageYOffset || 0;
		var scrollDirection = "down"; // initial: user is at the top, "down" is the only way to go
		var rafScheduled = false;
		// `hasUserScrolled` gates the directional logic. On a fresh
		// page load at scrollY = 0 the bottom-1/3 reading zone is empty
		// (Step 2's title sits exactly at vh, the zone's lower bound),
		// so we want the explicit "main = active" init to win. The first
		// real scroll event flips the flag and the directional logic
		// takes over from then on.
		var hasUserScrolled = false;

		function pickActiveStep(vh) {
			var bestStep = null;
			var bestTop = scrollDirection === "down" ? -Infinity : Infinity;

			stepEls.forEach(function (step, idx) {
				var titleEl = titles[idx];
				if (!titleEl) return;
				var top = titleEl.getBoundingClientRect().top;

				if (scrollDirection === "down") {
					// Title in the bottom 1/3 of the viewport
					// (zoneMin = 2/3 vh, zoneMax = vh, inclusive).
					// The step is "active" the moment its title
					// reaches the top edge of the bottom strip
					// (2/3 vh) and stays active until the title
					// scrolls off the bottom (vh). Pick the title
					// with the LARGEST top — the most recent
					// arrival from the bottom edge.
					if (top >= (vh * 2) / 3 && top <= vh) {
						if (top > bestTop) {
							bestTop = top;
							bestStep = step;
						}
					}
				} else {
					// Title in the top 1/3 of the viewport
					// (zoneMin = 0, zoneMax = vh / 3, inclusive).
					// Pick the title with the SMALLEST top — the
					// one that just re-entered from the top edge.
					if (top >= 0 && top <= vh / 3) {
						if (top < bestTop) {
							bestTop = top;
							bestStep = step;
						}
					}
				}
			});

			// Fallback: when no title in reading zone (refresh
			// mid-page, fast scroll), pick step whose bounding
			// rect contains the viewport center.
			if (!bestStep) {
				var vpCenter = vh / 2;
				stepEls.forEach(function (step) {
					var r = step.getBoundingClientRect();
					if (r.top <= vpCenter && r.bottom >= vpCenter) {
						bestStep = step;
					}
				});
			}

			return bestStep;
		}

		function update() {
			rafScheduled = false;

			var currentScrollY = window.scrollY || window.pageYOffset || 0;
			var vh = window.innerHeight || 1;

			// Update direction only on actual movement; equal scroll
			// (e.g. horizontal scroll) leaves the previous direction
			// in place so we don't flip-flop at rest.
			if (currentScrollY > lastScrollY) {
				scrollDirection = "down";
			} else if (currentScrollY < lastScrollY) {
				scrollDirection = "up";
			}
			lastScrollY = currentScrollY;

			// Apply directional panel switch only after the first
			// scroll event. Before that, the explicit "main = active"
			// init state holds (handles both fresh load at scrollY=0
			// and browser-restored scroll).
			if (hasUserScrolled) {
				var bestStep = pickActiveStep(vh);

				if (bestStep) {
					var stepKey = bestStep.getAttribute("data-step");
					if (stepKey && stepKey !== activeKey) {
						Object.keys(panels).forEach(function (key) {
							setPanelActive(panels[key], key === stepKey);
						});
						stepEls.forEach(function (el) {
							el.classList.toggle(
								"is-active",
								el.getAttribute("data-step") === stepKey,
							);
						});
						activeKey = stepKey;
						state.currentStep = stepKey;
						updateSwiperOnPanelSwitch(stepKey);
					}
				}
				// If bestStep is null, the zone is empty — keep the
				// current active step rather than tearing down the
				// visual state mid-scroll.
			}

			// Sticky active state (border + shadow feedback) — always
			// updated so the visual ring tracks the sticky center even
			// before the user has scrolled.
			if (stickyContainer) {
				var rect = stickyContainer.getBoundingClientRect();
				var center = rect.top + rect.height / 2;
				var nearCenter = Math.abs(center - vh / 2) < 8;
				var inMiddle = rect.top < vh * 0.85 && rect.bottom > vh * 0.15;
				stickyInner.classList.toggle(
					"is-active",
					nearCenter && inMiddle,
				);
			}
		}

		function onScroll() {
			hasUserScrolled = true;
			if (!rafScheduled) {
				rafScheduled = true;
				requestAnimationFrame(update);
			}
		}

		window.addEventListener("scroll", onScroll, {
			signal: signal,
			passive: true,
		});

		// On refresh with browser-restored scroll position,
		// run one initial calcn so left panel matches actual
		// scroll position immediately (not stuck on "main").
		if (lastScrollY > 0) {
			hasUserScrolled = true;
			requestAnimationFrame(update);
		}
	}

	// Toggle a single panel's visible/hidden state via Tailwind opacity
	// classes. We don't toggle `display` so Swiper dimensions stay valid.
	function setPanelActive(el, isActive) {
		if (!el) return;
		if (isActive) {
			el.classList.remove(
				"bx-opacity-0",
				"bx-invisible",
				"bx-pointer-events-none",
			);
			el.classList.add(
				"bx-opacity-100",
				"bx-visible",
				"bx-pointer-events-auto",
			);
			el.setAttribute("data-panel-active", "true");
		} else {
			el.classList.add(
				"bx-opacity-0",
				"bx-invisible",
				"bx-pointer-events-none",
			);
			el.classList.remove(
				"bx-opacity-100",
				"bx-visible",
				"bx-pointer-events-auto",
			);
			el.setAttribute("data-panel-active", "false");
		}
	}

	// Backwards-compatible wrapper — kept for any existing call sites.
	function switchPanel(step, panels) {
		Object.keys(panels).forEach(function (key) {
			setPanelActive(panels[key], key === step);
		});
		state.currentStep = step;
		updateSwiperOnPanelSwitch(step);
	}

	// In the new layout, panels are always in the DOM (opacity-toggled),
	// so a Swiper `update()` is sufficient. Only the main/gift panels host
	// Swipers; the summary panel has none.
	function updateSwiperOnPanelSwitch(panelKey) {
		if (panelKey === "main") {
			[swipers.mainLeftBlack, swipers.mainLeftWhite].forEach(
				function (s) {
					if (s && !s.destroyed) s.update();
				},
			);
		} else if (panelKey === "gift") {
			if (swipers.giftLeft && !swipers.giftLeft.destroyed)
				swipers.giftLeft.update();
			if (swipers.giftLeftThumbs && !swipers.giftLeftThumbs.destroyed)
				swipers.giftLeftThumbs.update();
		}
	}

	// =========================================================================
	// GSAP ScrollTo — Back / Continue Navigation
	// =========================================================================
	function initNavigationButtons() {
		// Register ScrollToPlugin
		if (
			typeof gsap !== "undefined" &&
			typeof ScrollToPlugin !== "undefined"
		) {
			gsap.registerPlugin(ScrollToPlugin);
		}

		document
			.querySelectorAll('[data-action="continue"], [data-action="back"]')
			.forEach(function (btn) {
				btn.addEventListener(
					"click",
					function (e) {
						e.preventDefault();
						var target = btn.getAttribute("data-target");
						if (!target) return;

						var targetEl = document.getElementById(
							"krono-step-" + target,
						);
						if (!targetEl) return;

						if (state.isMobile) {
							// On mobile, scroll the window to the step
							if (
								typeof gsap !== "undefined" &&
								typeof ScrollToPlugin !== "undefined"
							) {
								gsap.to(window, {
									duration: 0.6,
									scrollTo: { y: targetEl, offsetY: 20 },
									ease: "power2.inOut",
								});
							} else {
								targetEl.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}
						} else {
							// On desktop, scroll the window (body scrolls naturally)
							var scrollTarget = window;
							if (
								typeof gsap !== "undefined" &&
								typeof ScrollToPlugin !== "undefined"
							) {
								gsap.to(scrollTarget, {
									duration: 0.6,
									scrollTo: { y: targetEl, offsetY: 80 },
									ease: "power2.inOut",
								});
							} else {
								targetEl.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}
						}
					},
					{ signal: signal },
				);
			});
	}

	// =========================================================================
	// Availability Gate — disable action buttons when either product
	// has zero available variants
	// =========================================================================
	function checkAllVariantsUnavailable(role) {
		var variants =
			role === "main"
				? window.__KRONO_MAIN_VARIANTS__
				: window.__KRONO_GIFT_VARIANTS__;
		if (!variants || !variants.length) return true;
		return variants.every(function (v) {
			return !v.available;
		});
	}

	function updateActionButtons() {
		var mainAllDead = checkAllVariantsUnavailable("main");
		var giftAllDead = checkAllVariantsUnavailable("gift");
		var allUnavailable = mainAllDead || giftAllDead;

		var addCartBtn = document.getElementById("krono-add-cart");
		var checkoutBtn = document.getElementById("krono-direct-checkout");
		var unavailableText =
			(addCartBtn && addCartBtn.getAttribute("data-unavailable-text")) ||
			"Unavailable";

		if (allUnavailable) {
			if (addCartBtn) {
				addCartBtn.disabled = true;
				addCartBtn.textContent = unavailableText;
				addCartBtn.classList.add(
					"bx-opacity-50",
					"bx-cursor-not-allowed",
				);
			}
			if (checkoutBtn) {
				checkoutBtn.style.display = "none";
			}
		} else {
			if (addCartBtn) {
				addCartBtn.disabled = false;
				addCartBtn.textContent =
					addCartBtn.getAttribute("data-default-text") ||
					addCartBtn.textContent;
				addCartBtn.classList.remove(
					"bx-opacity-50",
					"bx-cursor-not-allowed",
				);
			}
			if (checkoutBtn) {
				checkoutBtn.style.display = "";
			}
		}
	}

	// =========================================================================
	// Add to Cart
	// =========================================================================
	function initAddToCart() {
		var addCartBtn = document.getElementById("krono-add-cart");
		if (addCartBtn) {
			addCartBtn.addEventListener(
				"click",
				function () {
					addToCart(false);
				},
				{ signal: signal },
			);
		}

		var checkoutBtn = document.getElementById("krono-direct-checkout");
		if (checkoutBtn) {
			checkoutBtn.addEventListener(
				"click",
				function () {
					addToCart(true);
				},
				{ signal: signal },
			);
		}
	}

	function addToCart(directCheckout) {
		if (state.isLoading) return;
		if (!state.mainVariantId || !state.giftVariantId) return;

		state.isLoading = true;

		// Generate bundle ID
		state.bundleId =
			"K-" +
			Date.now().toString(36) +
			"-" +
			Math.random().toString(36).slice(2, 6);

		// Loading state
		var btn = directCheckout
			? document.getElementById("krono-direct-checkout")
			: document.getElementById("krono-add-cart");
		var originalHTML = btn ? btn.innerHTML : "";

		if (btn) {
			btn.disabled = true;
			btn.innerHTML =
				'<span class="krono-spinner bx-inline-block bx-w-4 bx-h-4 bx-border-2 bx-border-white bx-border-t-transparent bx-rounded-full bx-mr-2 bx-align-middle"></span> Adding...';
		}

		// Build items array for single /cart/add.js request
		var items = [
			{
				id: state.giftVariantId,
				quantity: 1,
				properties: {
					_krono_bundle: state.bundleId,
					_krono_role: "gift",
				},
			},
			{
				id: state.mainVariantId,
				quantity: 1,
				properties: {
					_krono_bundle: state.bundleId,
					_krono_role: "main",
				},
			},
		];

		// Single AJAX request (not two sequential calls)
		// Ref: https://shopify.dev/docs/api/ajax/reference/cart
		fetch("/cart/add.js", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ items: items }),
		})
			.then(function (res) {
				if (!res.ok) {
					return res.json().then(function (err) {
						var msg =
							err.description ||
							err.message ||
							"Failed to add items to cart";
						throw new Error(msg);
					});
				}
				return res.json();
			})
			.then(function (data) {
				// Success
				state.isLoading = false;
				if (btn) {
					btn.disabled = false;
					btn.innerHTML = originalHTML;
				}

				if (directCheckout) {
					window.location.href = "/checkout";
					return;
				}

				// Update header cart count
				fetch("/cart.js")
					.then(function (r) {
						return r.json();
					})
					.then(function (cartData) {
						var cartCountEl =
							document.querySelector("[data-cart-count]");
						if (cartCountEl && cartData.item_count !== undefined) {
							cartCountEl.textContent = cartData.item_count;
						}
					})
					.catch(function () {
						/* silent */
					});

				// Build product data from response items (includes discount info)
				if (window.cartAddedModal && data.items) {
					var products = data.items.map(function (item) {
						var productData = {
							image:
								item.image ||
								(item.featured_image
									? item.featured_image.url
									: "") ||
								"",
							title: item.product_title || item.title || "",
							variant_title:
								item.variant_title &&
								item.variant_title !== "Default Title"
									? item.variant_title
									: "",
							price: formatMoney(
								item.discounted_price != null
									? item.discounted_price
									: item.final_price,
							),
							quantity: item.quantity || 1,
						};

						// Attach discount info when present
						if (item.total_discount > 0) {
							productData.total_discount = item.total_discount;
							productData.original_price = formatMoney(
								item.original_price,
							);
							productData.discounted_price =
								item.discounted_price;
						}

						return productData;
					});

					if (products.length) {
						window.cartAddedModal.open({ products: products });
					}
				}
			})
			.catch(function (error) {
				// Error — restore button
				console.error("[Krono] Add to cart failed:", error);
				state.isLoading = false;
				if (btn) {
					btn.disabled = false;
					btn.innerHTML = originalHTML;
				}
				showErrorToast(error.message || "Failed to add items to cart");
			});
	}

	// =========================================================================
	// Ship-to Country Detection
	// =========================================================================
	function fetchShipTo() {
		fetch("/browsing_context_suggestions.json", { signal: signal })
			.then(function (res) {
				return res.json();
			})
			.then(function (data) {
				var country =
					data.detected_values && data.detected_values.country_name;
				if (country) {
					var el = document.querySelector("[data-ship-text]");
					if (el) {
						el.textContent = country;
					}
				}
			})
			.catch(function () {
				// Silent fail — keep default text
			});
	}

	// =========================================================================
	// Zoom Modal — click on swiper images
	// =========================================================================
	function initZoomModal() {
		document
			.querySelectorAll(".krono-main-swiper .swiper-slide img")
			.forEach(function (img) {
				img.addEventListener(
					"click",
					function () {
						if (typeof window.openImageModal === "function") {
							var src = img.currentSrc || img.src;
							var alt = img.alt || "Product image";
							window.openImageModal(src, alt);
						}
					},
					{ signal: signal },
				);
			});
	}

	// =========================================================================
	// Error Toast
	// =========================================================================
	function showErrorToast(message) {
		var existing = document.querySelector(".krono-error-toast");
		if (existing) existing.remove();

		var toast = document.createElement("div");
		toast.className =
			"krono-error-toast bx-fixed bx-top-4 bx-right-4 bx-bg-red-500 bx-text-white bx-px-4 bx-py-3 bx-rounded-lg bx-shadow-lg bx-z-50 bx-text-sm";
		toast.textContent = message;
		document.body.appendChild(toast);
		setTimeout(function () {
			if (toast.parentNode) toast.parentNode.removeChild(toast);
		}, 5000);
	}

	// =========================================================================
	// Mobile Detection — handle resize
	// =========================================================================
	function handleResize() {
		var wasMobile = state.isMobile;
		state.isMobile = window.innerWidth < 751;

		// If crossing the breakpoint, reload to reinitialize swipers correctly
		if (wasMobile !== state.isMobile) {
			// Destroy swipers
			destroySwipers();
			// Reinit
			initAllSwipers();
		}
	}

	function destroySwipers() {
		Object.keys(swipers).forEach(function (key) {
			if (swipers[key] && !swipers[key].destroyed) {
				swipers[key].destroy(true, true);
				swipers[key] = null;
			}
		});
	}

	// =========================================================================
	// Auto-select first available variant on load
	// -------------------------------------------------------------------------
	// Mirrors Shopify's `selected_or_first_available_variant` Liquid filter:
	// for each option group, prefer the value Liquid pre-highlighted
	// (bx-border-theme-pink), otherwise fall back to the first non-disabled
	// button. Reuses handleVariantSelect so state, price display, hidden
	// input and summary all wire up identically to a user click.
	// =========================================================================
	function autoSelectFirstAvailable() {
		["main", "gift"].forEach(function (role) {
			var picker = document.getElementById(
				role === "main" ? "krono-main" : "krono-gift",
			);
			if (!picker) return;

			// Skip if a prior interaction already wired this role's state
			if (
				(role === "main" && state.mainVariantId) ||
				(role === "gift" && state.giftVariantId)
			)
				return;

			picker.querySelectorAll(".option-group").forEach(function (group) {
				// Find the pre-selected button (any type) or fall back to first available
				var target =
					group.querySelector(
						".krono-option-btn.bx-border-theme-pink:not([disabled])",
					) ||
					group.querySelector(
						".krono-color-swatch--active:not([disabled])",
					) ||
					group.querySelector(
						".krono-ship-btn--active:not([disabled])",
					) ||
					group.querySelector(".krono-variant-btn:not([disabled])");
				if (target) target.click();
			});
		});
	}

	// =========================================================================
	// Ticket machine animation — play once, IntersectionObserver-triggered
	// =========================================================================
	function initTicketAnimation() {
		var ticket = document.getElementById("krono-summary-box__ticket");
		var inner = document.getElementById("krono-ticket-inner");
		if (!ticket || !inner) return;

		// Start above the clip window, invisible through overflow
		gsap.set(inner, { yPercent: -100 });

		var animated = false;

		var observer = new IntersectionObserver(
			function (entries) {
				if (animated) return;
				entries.forEach(function (entry) {
					if (entry.isIntersecting && !animated) {
						animated = true;
						observer.disconnect();
						gsap.to(inner, {
							yPercent: 0,
							duration: 1,
							// ease: "power2.out",
							ease: "power3.inOut",
						});
					}
				});
			},
			{
				// Trigger when ticket top enters bottom 1/3 of viewport
				rootMargin: "0px 0px -33% 0px",
				threshold: 0,
			},
		);

		observer.observe(ticket);
	}

	// =========================================================================
	// Initialization
	// =========================================================================
	function init() {
		initAllSwipers();
		initVariantPickers();
		autoSelectFirstAvailable();
		initScrollSpy();
		initTicketAnimation();
		initNavigationButtons();
		initAddToCart();
		initZoomModal();
		fetchShipTo();
		updateActionButtons();

		// Handle resize
		window.addEventListener("resize", handleResize, { signal: signal });
	}

	// =========================================================================
	// Cleanup
	// =========================================================================
	window.destroyKronoProduct = function () {
		abortController.abort();
		destroySwipers();

		// Clear state references
		state = null;
		swipers = null;

		console.log("[Krono] Destroyed and cleaned up");
	};

	// =========================================================================
	// Start
	// =========================================================================
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
